import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const MILESTONES = [
  { year: '2021', event: 'META66.cn 正式创立，聚焦元宇宙垂直资讯', color: '#00FF88', detail: '由一批来自互联网、区块链、XR领域的连续创业者共同创立，初期专注于元宇宙行业资讯与深度分析。' },
  { year: '2022', event: '生态伙伴突破100家，覆盖VR/AR/区块链全产业链', color: '#FFD700', detail: '与Meta、HTC、字节跳动、腾讯等头部企业建立战略合作，生态伙伴覆盖XR硬件、区块链基础设施、数字内容等全产业链。' },
  { year: '2023', event: '推出元宇宙产业导航平台，用户突破500万', color: '#FF6B9D', detail: '发布全球首个元宇宙产业导航平台，汇聚3000+元宇宙项目数据库，月活用户突破500万，成为行业最权威的信息枢纽。' },
  { year: '2024', event: '6G技术研究院成立，Web4.0协议联盟发起', color: '#00FFCC', detail: '联合中国移动、华为、中科院等机构成立6G技术研究院，发起Web4.0协议联盟，推动下一代互联网标准制定。' },
  { year: '2025', event: '全球节点网络部署完成，数字经济生态全面启动', color: '#A855F7', detail: '在深圳、上海、北京、香港、新加坡、东京、旧金山、纽约、伦敦等12个城市完成全球节点网络部署，数字经济生态全面启动。' },
  { year: '2026+', event: '迈向7G+Web5.0时代，构建数字文明新基础设施', color: '#FF6B35', detail: '启动7G技术预研与Web5.0协议开发，目标在2030年前成为全球最具影响力的元宇宙生态平台，服务10亿用户。' },
];

const CORE_VALUES = [
  { icon: 'ri-eye-line', title: '前瞻视野', desc: '持续追踪全球元宇宙、6G-7G、Web4.0-5.0最前沿动态，为行业提供最具价值的洞察', color: '#00FF88' },
  { icon: 'ri-links-line', title: '生态连接', desc: '汇聚产业链上下游500+核心机构，打造元宇宙领域最具影响力的生态网络', color: '#FFD700' },
  { icon: 'ri-shield-check-line', title: '价值投资', desc: '深度分析元宇宙赛道投融资机会，为投资者提供专业、可靠的决策参考', color: '#FF6B9D' },
  { icon: 'ri-rocket-line', title: '创新驱动', desc: '支持元宇宙创业者与开发者，加速下一代数字文明基础设施的落地与普及', color: '#00FFCC' },
  { icon: 'ri-team-line', title: '社区共建', desc: '构建全球最活跃的元宇宙开发者与创作者社区，共同推动数字文明进化', color: '#A855F7' },
  { icon: 'ri-leaf-line', title: '可持续发展', desc: '以绿色技术和循环经济理念推动元宇宙发展，确保数字文明与自然生态和谐共存', color: '#FF6B35' },
];

const META_CONCEPTS = [
  {
    id: 'definition', icon: 'ri-planet-line', color: '#00FF88', title: '元宇宙的本质定义',
    content: '元宇宙（Metaverse）并非单一技术，而是一个由多种前沿技术融合构建的"持久化、去中心化、沉浸式"数字平行宇宙。这一概念最早由科幻作家Neal Stephenson在1992年小说《雪崩》中提出，描述了一个人类以数字化身（Avatar）生活其中的虚拟世界。\n\n元宇宙的核心特征包括：永续性（不因用户离线而消失）、实时性（与现实世界同步运行）、互操作性（跨平台资产与身份互通）、去中心化（无单一主体控制）、经济系统（完整的数字价值创造与流通体系）。',
    img: 'https://public.readdy.ai/ai/img_res/e47ea562f36a40374bca4aad9d04d320.jpg',
  },
  {
    id: 'logic', icon: 'ri-cpu-line', color: '#FFD700', title: '底层逻辑：技术三角',
    content: '元宇宙的底层逻辑建立在三大技术支柱之上：\n\n① 感知层（XR技术）：VR/AR/MR/XR设备构建人机交互界面，让人类感官延伸至数字空间。裸眼3D与脑机接口将最终消除设备边界，实现"无感知"沉浸。\n\n② 计算层（AI+量子计算）：AI大模型驱动虚拟世界的智能生成与实时渲染，量子计算提供超越经典计算机的算力支撑，使复杂物理模拟与全球级并发成为可能。\n\n③ 传输层（6G-7G网络）：太赫兹频段的6G网络提供1Tbps峰值速率与亚毫秒延迟，是元宇宙实时交互的血管系统。7G将进一步实现意识级通信。',
    img: 'https://readdy.ai/api/search-image?query=AI%20quantum%20computing%20XR%20technology%20triangle%20foundation%20glowing%20neon%20dark%20background%20futuristic%20technology&width=600&height=380&seq=about-logic-001&orientation=landscape',
  },
  {
    id: 'economy', icon: 'ri-coin-line', color: '#FF6B9D', title: '数字经济：价值重构',
    content: '元宇宙将重构人类的价值创造与分配体系。区块链技术赋予数字资产唯一性与可验证所有权，NFT让创作者直接获得价值回报，DeFi协议构建无需中介的金融体系。\n\n在元宇宙经济中，虚拟土地、数字艺术、游戏道具、社交身份等数字资产将与实体资产同等重要。预计到2030年，全球元宇宙经济规模将超过30万亿美元，虚拟世界的GDP将超越多数实体国家。\n\nMETA66深度布局这一价值重构进程，为投资者、创作者与开发者提供最前沿的生态连接与价值发现服务。',
    img: 'https://readdy.ai/api/search-image?query=blockchain%20NFT%20digital%20economy%20cryptocurrency%20value%20glowing%20neon%20dark%20background%20futuristic%20technology&width=600&height=380&seq=about-econ-001&orientation=landscape',
  },
  {
    id: 'evolution', icon: 'ri-git-branch-line', color: '#00FFCC', title: '演化进程：从概念到文明',
    content: '元宇宙的演化遵循"基础设施→内容生态→经济系统→社会形态"的四阶段路径：\n\n第一阶段（2020-2025）：基础设施建设期。6G网络商用、XR设备普及、区块链基础协议成熟，元宇宙从概念走向可用。\n\n第二阶段（2025-2030）：内容生态爆发期。AI生成内容（AIGC）降低创作门槛，数字孪生城市全面落地，元宇宙日活用户突破10亿。\n\n第三阶段（2030-2040）：经济系统成熟期。虚实资产互通，数字身份成为法律认可的主权身份，元宇宙经济规模超越多数实体经济体。\n\n第四阶段（2040+）：社会形态重构期。脑机接口普及，虚实边界消融，人类文明进入"数字-物理融合"的新纪元。',
    img: 'https://readdy.ai/api/search-image?query=metaverse%20evolution%20timeline%20future%20civilization%20digital%20glowing%20neon%20dark%20background%20futuristic%20technology&width=600&height=380&seq=about-evol-001&orientation=landscape',
  },
];

const TEAM_STATS = [
  { label: '全球活跃用户', value: '3,200万+', color: '#00FF88', icon: 'ri-user-line' },
  { label: '生态合作伙伴', value: '500+', color: '#FFD700', icon: 'ri-building-line' },
  { label: '全球节点城市', value: '12座', color: '#FF6B9D', icon: 'ri-map-pin-line' },
  { label: '原创深度内容', value: '12,000+', color: '#00FFCC', icon: 'ri-article-line' },
  { label: '开发者社区', value: '80万+', color: '#A855F7', icon: 'ri-code-line' },
  { label: '年研究报告', value: '200+', color: '#FF6B35', icon: 'ri-file-chart-line' },
];

export default function AboutPage() {
  const navigate = useNavigate();
  const [activeConcept, setActiveConcept] = useState('definition');
  const [activeMilestone, setActiveMilestone] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'META66关于我们 | 元宇宙科技平台 · 使命愿景 · 发展历程 · 核心价值观';
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://meta66.cn';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': `${siteUrl}/about#webpage`,
      url: `${siteUrl}/about`,
      name: 'META66关于我们 | 元宇宙科技平台 · 使命愿景 · 发展历程 · 核心价值观',
      description: 'META66是中国领先的元宇宙垂直综合资讯与生态平台，致力于汇聚元宇宙产业链核心力量，推动6G-7G网络、Web3.0-Web5.0协议、XR沉浸技术与数字经济的深度融合与创新发展。',
      inLanguage: 'zh-CN',
      dateModified: '2026-04-04',
      isPartOf: { '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: `${siteUrl}/about` },
        ],
      },
    };
    const existingScript = document.getElementById('schema-about-jsonld');
    if (existingScript) existingScript.remove();
    const script = document.createElement('script');
    script.id = 'schema-about-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { const s = document.getElementById('schema-about-jsonld'); if (s) s.remove(); };
  }, []);

  const currentConcept = META_CONCEPTS.find(c => c.id === activeConcept)!;

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', 'PingFang SC', sans-serif" }}>
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line" />
            <span className="text-sm">返回首页</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #00FF88, #FFD700)' }}>
              <i className="ri-planet-line text-black text-sm" />
            </div>
            <span className="font-bold text-gray-900">关于 META66</span>
          </div>
          <div className="text-sm text-gray-400">META66 · 使命愿景</div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative h-80 overflow-hidden">
        <img src="https://readdy.ai/api/search-image?query=futuristic%20metaverse%20virtual%20city%20holographic%20buildings%20glowing%20colorful%20neon%20lights%20digital%20world%20immersive%20environment%20sci-fi%20dark%20background%20cinematic%20wide&width=1920&height=500&seq=about-hero-001&orientation=landscape" alt="关于META66" className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        <div className="absolute inset-0 flex items-center px-16">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-500/20 border border-green-500/40 mb-4">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-green-400 text-sm font-medium">中国领先元宇宙生态平台</span>
            </div>
            <h1 className="text-5xl font-black text-white mb-3" style={{ fontFamily: "'Orbitron', monospace" }}>ABOUT META66</h1>
            <p className="text-gray-300 text-lg max-w-xl">构建下一代数字文明 · 连接元宇宙产业链核心力量</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <div className="p-8 rounded-3xl border border-green-100" style={{ background: 'linear-gradient(135deg, #00FF8808, #00FFCC05)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#00FF8820' }}>
                <i className="ri-flag-line text-xl" style={{ color: '#00FF88' }} />
              </div>
              <h2 className="text-xl font-black text-gray-900">使命 · Mission</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              META66的使命是成为元宇宙时代最权威的知识枢纽与生态连接器。我们深信，元宇宙不仅是技术的集合，更是人类文明进化的下一个里程碑。通过汇聚全球最前沿的元宇宙资讯、深度分析与产业洞察，META66致力于帮助每一位参与者——无论是技术创新者、投资决策者还是普通用户——在这场数字文明变革中找到自己的坐标与价值。
            </p>
          </div>
          <div className="p-8 rounded-3xl border border-yellow-100" style={{ background: 'linear-gradient(135deg, #FFD70008, #FF6B3505)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#FFD70020' }}>
                <i className="ri-eye-line text-xl" style={{ color: '#FFD700' }} />
              </div>
              <h2 className="text-xl font-black text-gray-900">愿景 · Vision</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              我们的愿景是构建一个开放、包容、可持续的元宇宙生态系统。在这个生态中，技术创新与人文关怀并重，数字资产与现实价值互通，虚拟身份与现实身份平等受到尊重。META66将持续引领元宇宙从概念走向现实，从小众走向主流，最终成为人类数字文明的核心基础设施之一。到2030年，我们的目标是服务全球1亿元宇宙参与者，连接10,000+生态伙伴。
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-16">
          {TEAM_STATS.map(stat => (
            <div key={stat.label} className="p-5 rounded-2xl text-center" style={{ background: `${stat.color}08`, border: `1px solid ${stat.color}20` }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background: `${stat.color}15` }}>
                <i className={`${stat.icon} text-lg`} style={{ color: stat.color }} />
              </div>
              <div className="text-2xl font-black text-gray-900 mb-1">{stat.value}</div>
              <div className="text-gray-500 text-xs">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Metaverse Concepts */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-black text-gray-900 mb-2">元宇宙底层概念深度解析</h2>
            <p className="text-gray-500 text-sm">理解元宇宙的本质、底层逻辑与演化进程</p>
          </div>

          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {META_CONCEPTS.map(c => (
              <button key={c.id} onClick={() => setActiveConcept(c.id)}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-full border"
                style={{
                  borderColor: activeConcept === c.id ? c.color : '#e5e7eb',
                  background: activeConcept === c.id ? `${c.color}12` : 'transparent',
                  color: activeConcept === c.id ? c.color : '#888',
                }}>
                <i className={`${c.icon} text-xs`} />
                {c.title.split('：')[0]}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="relative rounded-2xl overflow-hidden" style={{ minHeight: '320px' }}>
              <img src={currentConcept.img} alt={currentConcept.title} className="w-full h-full object-cover object-top" style={{ minHeight: '320px' }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-black/70 backdrop-blur-sm border" style={{ borderColor: `${currentConcept.color}30` }}>
                  <i className={`${currentConcept.icon} text-sm`} style={{ color: currentConcept.color }} />
                  <span className="text-white/80 text-xs font-semibold">{currentConcept.title}</span>
                </div>
              </div>
            </div>
            <div className="p-6 rounded-2xl bg-gray-50 border border-gray-100">
              <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
                <i className={`${currentConcept.icon} text-sm`} style={{ color: currentConcept.color }} />
                {currentConcept.title}
              </h3>
              <div className="space-y-3">
                {currentConcept.content.split('\n\n').map((para, i) => (
                  <p key={i} className="text-gray-600 text-sm leading-relaxed">
                    {para.split('\n').map((line, j) => (
                      <span key={j}>
                        {line.startsWith('①') || line.startsWith('②') || line.startsWith('③') || line.startsWith('第') ? (
                          <strong style={{ color: currentConcept.color }}>{line}</strong>
                        ) : line}
                        {j < para.split('\n').length - 1 && <br />}
                      </span>
                    ))}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div className="mb-16">
          <h2 className="text-3xl font-black text-gray-900 mb-8 text-center">核心价值观</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CORE_VALUES.map(v => (
              <div key={v.title} className="flex gap-4 p-6 rounded-2xl border border-gray-100 hover:border-gray-200 transition-all hover:-translate-y-1">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${v.color}15` }}>
                  <i className={`${v.icon} text-xl`} style={{ color: v.color }} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{v.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="mb-16">
          <h2 className="text-3xl font-black text-gray-900 mb-8 text-center">发展历程</h2>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 -translate-x-1/2 hidden md:block" />
            {MILESTONES.map((m, i) => (
              <div key={m.year} className={`flex flex-col md:flex-row items-center gap-4 md:gap-8 mb-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                  <div
                    className="inline-block p-5 rounded-2xl cursor-pointer transition-all hover:-translate-y-1"
                    style={{
                      background: activeMilestone === i ? `${m.color}10` : '#f9f9f9',
                      border: `1px solid ${activeMilestone === i ? m.color : '#eee'}`,
                    }}
                    onClick={() => setActiveMilestone(activeMilestone === i ? null : i)}>
                    <div className="font-black text-2xl mb-1" style={{ color: m.color }}>{m.year}</div>
                    <p className="text-gray-700 text-sm font-medium mb-2">{m.event}</p>
                    {activeMilestone === i && (
                      <p className="text-gray-500 text-xs leading-relaxed mt-2 border-t border-gray-100 pt-2">{m.detail}</p>
                    )}
                    <p className="text-xs mt-1" style={{ color: m.color }}>{activeMilestone === i ? '收起详情 ↑' : '点击查看详情 ↓'}</p>
                  </div>
                </div>
                <div className="w-4 h-4 rounded-full border-4 border-white z-10 flex-shrink-0 hidden md:block" style={{ background: m.color }} />
                <div className="flex-1 hidden md:block" />
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="rounded-3xl p-10 text-center" style={{ background: 'linear-gradient(135deg, #00FF8810, #FFD70008, #FF6B9D08)' }}>
          <h2 className="text-3xl font-black text-gray-900 mb-4">加入 META66 生态</h2>
          <p className="text-gray-500 mb-8 max-w-xl mx-auto">无论你是开发者、投资者、创作者还是元宇宙爱好者，META66都有适合你的位置</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/contact')} className="px-8 py-4 rounded-2xl font-bold text-white whitespace-nowrap cursor-pointer" style={{ background: 'linear-gradient(135deg, #00FF88, #00FFCC)', color: '#000' }}>
              联系我们
            </button>
            <button onClick={() => navigate('/news')} className="px-8 py-4 rounded-2xl font-bold whitespace-nowrap cursor-pointer border border-gray-200 text-gray-700 hover:border-gray-300 transition-colors">
              浏览资讯
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
